import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SectionTitle } from "@/components/dashboard/widgets";
import { ShieldCheck, ShieldAlert, ShieldX, AlertTriangle, Loader2, Search, Eye, Check, ArrowUpRight } from "lucide-react";
import { RowBin, ClearArea } from "@/components/admin/Purge";
import { toast } from "sonner";

const SEV = {
  info:     { label: "Info",     border: "border-slate-200",  bg: "bg-slate-50",     text: "text-slate-700",   Icon: ShieldCheck, dot: "bg-slate-400" },
  warning:  { label: "Warning",  border: "border-amber-200",  bg: "bg-amber-50",     text: "text-amber-800",   Icon: ShieldAlert, dot: "bg-amber-500" },
  critical: { label: "Critical", border: "border-rose-200",   bg: "bg-rose-50",      text: "text-rose-700",    Icon: ShieldX,     dot: "bg-rose-500" },
};

export default function AdminCompliance() {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [sevFilter, setSevFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);

  const refresh = () => api.get("/admin/compliance").then(r => setEvents(r.data?.events || []));
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return events.filter(e => {
      if (sevFilter !== "all" && (e.severity || "info") !== sevFilter) return false;
      if (statusFilter === "open" && e.resolved) return false;
      if (statusFilter === "resolved" && !e.resolved) return false;
      if (q && !JSON.stringify(e).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [events, search, sevFilter, statusFilter]);

  const counts = useMemo(() => ({
    open: events.filter(e => !e.resolved).length,
    critical: events.filter(e => (e.severity === "critical") && !e.resolved).length,
    warning: events.filter(e => (e.severity === "warning") && !e.resolved).length,
    total: events.length,
  }), [events]);

  const openDetail = async (id) => {
    setActiveId(id);
    setActiveDetail(null);
    try {
      const { data } = await api.get(`/admin/compliance/${id}`);
      setActiveDetail(data);
    } catch (e) {
      toast.error("Could not load event detail");
    }
  };

  const resolve = async (id) => {
    await api.post(`/admin/compliance/${id}/resolve`);
    toast.success("Resolved");
    refresh();
    if (activeId === id) setActiveId(null);
  };

  const escalate = async (id, severity) => {
    await api.post(`/admin/compliance/${id}/escalate`, { severity });
    toast.success(`Escalated to ${severity}`);
    refresh();
    openDetail(id);
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Compliance" subtitle="FCA-aware event log. Triage flagged leads, returned leads, and policy violations." actions={<ClearArea area="compliance" onDone={refresh} label="Clear event log" />} />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Open events" value={counts.open} accent="text-slate-900" />
        <Tile label="Critical · open" value={counts.critical} accent="text-rose-600" />
        <Tile label="Warning · open" value={counts.warning} accent="text-amber-600" />
        <Tile label="Total tracked" value={counts.total} accent="text-slate-900" />
      </div>

      {/* Filters */}
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="size-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events, publishers, leads…"
              className="pl-9 h-10"
              data-testid="compliance-search"
            />
          </div>
          <Select value={sevFilter} onValueChange={setSevFilter}>
            <SelectTrigger className="w-40 h-10" data-testid="compliance-sev-filter"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-10" data-testid="compliance-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open only</SelectItem>
              <SelectItem value="resolved">Resolved only</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Returned leads — dispute queue (iter61) */}
      <ReturnedLeadsQueue />

      {/* Events list */}
      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <ShieldCheck className="size-8 mx-auto mb-2 text-slate-300" />
              <div className="font-medium text-slate-700">No events match these filters</div>
              <div className="text-sm mt-1">Try clearing the search or switching to "All".</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map(e => {
                const meta = SEV[e.severity] || SEV.info;
                const Icon = meta.Icon;
                return (
                  <div key={e.id} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50/50 transition-colors" data-testid={`compliance-row-${e.id.slice(0,8)}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      <Icon className={`size-4 mt-0.5 ${meta.text} flex-shrink-0`} />
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-slate-900 truncate">
                          {(e.type || "event").replace(/_/g, " ")} {e.resolved && <span className="ml-2 text-[11px] text-emerald-700 font-normal">· Resolved</span>}
                        </div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5">
                          {new Date(e.created_at).toLocaleString("en-GB")}
                          {e.lead_ref && <span className="ml-2 font-mono">lead {e.lead_ref.slice(0, 8)}</span>}
                          {e.publisher_id && <span className="ml-2 font-mono">pub {e.publisher_id.slice(0, 8)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[10.5px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${meta.border} ${meta.bg} ${meta.text}`}>{meta.label}</span>
                      <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px]" onClick={() => openDetail(e.id)} data-testid={`compliance-view-${e.id.slice(0,8)}`}>
                        <Eye className="size-3" /> View
                      </Button>
                      {!e.resolved && (
                        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-[11.5px] border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => resolve(e.id)} data-testid={`compliance-resolve-${e.id.slice(0,8)}`}>
                          <Check className="size-3" /> Resolve
                        </Button>
                      )}
                      <RowBin area="compliance" id={e.id} name={(e.type || "event").replace(/_/g, " ")} onDone={refresh} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <Sheet open={!!activeId} onOpenChange={(o) => !o && setActiveId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="compliance-detail-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><AlertTriangle className="size-5 text-amber-600" /> Compliance event</SheetTitle>
            <SheetDescription>Full payload + linked lead, buyer, publisher.</SheetDescription>
          </SheetHeader>
          {!activeDetail ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="size-5 animate-spin mx-auto mb-2" /> Loading…</div>
          ) : (
            <div className="mt-4 space-y-4 text-[13px]">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Event</div>
                <div className="font-medium text-slate-900 mt-1">{(activeDetail.event?.type || "").replace(/_/g, " ")}</div>
                <div className="text-[11.5px] text-slate-500">Severity: <b className="capitalize">{activeDetail.event?.severity || "info"}</b> · Created: {new Date(activeDetail.event?.created_at).toLocaleString("en-GB")}</div>
                {activeDetail.event?.resolved && <div className="text-[11.5px] text-emerald-700 mt-1">Resolved by {activeDetail.event.resolved_by} at {new Date(activeDetail.event.resolved_at).toLocaleString("en-GB")}</div>}
              </div>

              {activeDetail.event?.payload && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Payload</div>
                  <pre className="text-[11.5px] bg-slate-50 p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(activeDetail.event.payload, null, 2)}</pre>
                </div>
              )}

              {activeDetail.lead && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Linked lead</div>
                  <div className="font-medium text-slate-900 mt-1">{activeDetail.lead.first_name} {activeDetail.lead.last_name}</div>
                  <div className="text-[11.5px] text-slate-500">{activeDetail.lead.email} · {activeDetail.lead.sector} · £{Number(activeDetail.lead.price || 0).toFixed(2)}</div>
                </div>
              )}

              {activeDetail.buyer && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Buyer</div>
                  <div className="font-medium text-slate-900 mt-1">{activeDetail.buyer.name || activeDetail.buyer.company || "—"}</div>
                  <div className="text-[11.5px] text-slate-500">{activeDetail.buyer.email}</div>
                </div>
              )}

              {activeDetail.publisher && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Publisher</div>
                  <div className="font-medium text-slate-900 mt-1">{activeDetail.publisher.company || activeDetail.publisher.name || "—"}</div>
                  <div className="text-[11.5px] text-slate-500">{activeDetail.publisher.email}</div>
                </div>
              )}

              {!activeDetail.event?.resolved && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={() => resolve(activeId)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="compliance-detail-resolve"><Check className="size-3.5" /> Mark resolved</Button>
                  {activeDetail.event?.severity !== "critical" && (
                    <Button variant="outline" onClick={() => escalate(activeId, "critical")} className="rounded-full text-rose-700 hover:bg-rose-50 border-rose-200" data-testid="compliance-detail-escalate"><ArrowUpRight className="size-3.5" /> Escalate to critical</Button>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <Card className="rounded-xl border-slate-200 shadow-soft">
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-semibold tabular-nums ${accent || "text-slate-900"}`}>{value}</div>
      </CardContent>
    </Card>
  );
}


function ReturnedLeadsQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/returned-leads?unresolved_only=true&days=30")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resolve = async (lead, outcome) => {
    setBusyId(lead.id);
    try {
      await api.post(`/admin/leads/${lead.id}/dispute-resolve`, { outcome, note });
      toast.success(`Dispute resolved — ${outcome === "publisher_wins" ? "publisher wins (clawback reversed)" : "buyer wins (return stands)"}`);
      setActiveLead(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) {
    return (
      <Card className="rounded-xl shadow-soft border-slate-200" data-testid="returned-queue-empty">
        <CardContent className="p-5 flex items-center gap-3 text-[13px] text-slate-600">
          <ShieldCheck className="size-5 text-emerald-500" />
          <span>No unresolved returned leads. Publisher disputes will appear here for adjudication.</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl shadow-soft border-rose-200/70" data-testid="returned-queue">
      <CardContent className="p-0">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2 bg-rose-50/30">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">Returned leads · dispute queue</div>
            <div className="text-[12px] text-slate-500">Buyer flagged these as quality issues. Adjudicate — publisher wins reverses the clawback + re-debits buyer; buyer wins keeps the refund.</div>
          </div>
          <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">{rows.length} open</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="returned-table">
            <thead className="bg-slate-50/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Lead</th>
                <th className="font-medium">Buyer</th>
                <th className="font-medium">Publisher</th>
                <th className="font-medium">Reason</th>
                <th className="font-medium text-right">Price</th>
                <th className="font-medium pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l, i) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`returned-row-${i}`}>
                  <td className="px-5 py-3.5">
                    <div className="text-[13px] font-medium text-slate-900">{(l.first_name || "—")} {(l.last_name || "")}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{l.id?.slice(0, 12)} · {l.sector}</div>
                  </td>
                  <td className="py-3.5 text-[12.5px] text-slate-700">{l.buyer_company || l.buyer_email || "—"}</td>
                  <td className="py-3.5 text-[12.5px] text-slate-700">{l.publisher_email || "—"}</td>
                  <td className="py-3.5 text-[12.5px] text-slate-700 max-w-[200px] truncate" title={l.returned_reason}>{l.returned_reason || "—"}</td>
                  <td className="py-3.5 text-right font-mono tabular-nums">£{Number(l.price || 0).toFixed(2)}</td>
                  <td className="py-3.5 pr-5 text-right">
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-[11.5px]" onClick={() => { setActiveLead(l); setNote(""); }} data-testid={`resolve-btn-${i}`}>
                      Resolve
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Sheet open={!!activeLead} onOpenChange={(o) => !o && setActiveLead(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md" data-testid="dispute-resolve-sheet">
          {activeLead && (
            <>
              <SheetHeader className="pb-3 border-b border-slate-100">
                <SheetTitle>Resolve return dispute</SheetTitle>
                <SheetDescription className="text-[12px]">
                  Lead <span className="font-mono">{activeLead.id?.slice(0, 12)}</span> · £{Number(activeLead.price || 0).toFixed(2)} · {activeLead.sector}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-4 py-5 text-[13px]">
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Buyer's return reason</div>
                  <div className="rounded-md bg-amber-50/60 border border-amber-200 px-3 py-2 text-amber-900">{activeLead.returned_reason || "No reason given"}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Buyer</div>
                  <div className="text-slate-900">{activeLead.buyer_company || activeLead.buyer_email}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Publisher</div>
                  <div className="text-slate-900">{activeLead.publisher_email}</div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase tracking-widest text-slate-500">Admin note (audit log)</label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. publisher provided trustedform cert showing consent + intent" data-testid="dispute-note-input" />
                </div>
                <div className="pt-2 grid grid-cols-2 gap-2">
                  <Button onClick={() => resolve(activeLead, "buyer_wins")} disabled={busyId === activeLead.id}
                          variant="outline" className="rounded-full" data-testid="dispute-buyer-wins">
                    Buyer wins
                  </Button>
                  <Button onClick={() => resolve(activeLead, "publisher_wins")} disabled={busyId === activeLead.id}
                          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dispute-publisher-wins">
                    Publisher wins
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  <span className="font-medium text-slate-700">Buyer wins</span>: refund stands. <span className="font-medium text-slate-700">Publisher wins</span>: refund reversed, buyer re-debited £{Number(activeLead.price || 0).toFixed(2)}, publisher ledger credited £{Number(activeLead.publisher_earning_gbp || 0).toFixed(2)}.
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
}
